/**
 * Build a human-readable label for a feature.
 *
 * If a `uniqueName` was assigned by `featureIdentifier.applyFeatureUniqueNames`
 * (e.g. "ester-1", "benzene ring", "6-membered ring"), that wins.
 *
 * Otherwise the function falls back to parsing the internal dot-notation name:
 *
 *   "ring.6"            → "6-membered ring"
 *   "ring.5"            → "5-membered ring"
 *   "ring.6.benzene"    → "benzene"  (last segment)
 *   "ring.3.epoxide"    → "epoxide"  (last segment)
 *   "chain.5"           → "5-carbon chain"
 *   "chain.2.ethyl"     → "ethyl group"
 *   "chain.1.methyl"    → "methyl group"
 *   "carbonyl.ester"    → "ester"   (last segment)
 *   "alcohol"           → "alcohol" (no dots — passthrough)
 *
 * @param internalName The dot-notation feature name (e.g. "carbonyl.ester")
 * @param uniqueName   The display name assigned by featureIdentifier (preferred)
 */
export function nameFeature(
    internalName: string,
    uniqueName?: string,
): string {
    if (uniqueName) {
        return uniqueName;
    }

    // Special-cased ring sizes — "ring.6" → "6-membered ring".
    // Sub-typed rings ("ring.6.benzene", "ring.3.epoxide") fall through to the
    // last-segment branch below so the chemical name wins.
    if (
        internalName.startsWith("ring.") &&
        !internalName.includes("benzene") &&
        !internalName.includes("epoxide")
    ) {
        const size = internalName.split(".")[1];
        return `${size}-membered ring`;
    }

    if (internalName.startsWith("chain.")) {
        const parts = internalName.split(".");
        if (parts[2]) {
            return `${parts[2]} group`;
        }
        return `${parts[1]}-carbon chain`;
    }

    // Default: return the last dot-separated segment so "carbonyl.ester" → "ester".
    const parts = internalName.split(".");
    return parts[parts.length - 1];
}
